<script lang="ts" setup>
import { closeModal } from '~/utils/Modal'
import RModal from '~/components/organs/Modal.vue'
import {
  PaymentFailCode,
  PAYMENT_EXTERNAL_REDIRECT_KEY,
  getPaymentFailCode,
} from '~/static/enums/payment-fail-code'
import { DESKTOP_WIDTH } from '~/static/window-sizing'

interface Props {
  title: string
  url: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'success'): void
  (e: 'fail', code: PaymentFailCode): void
}>()

const windowWidth = ref(DESKTOP_WIDTH)
const changeWindowWidth = () => {
  windowWidth.value = window.innerWidth
}

const formLoaded = ref(false)
onMounted(() => {
  let tinkoffScript = document.createElement('script')
  tinkoffScript.setAttribute('src', 'https://integrationjs.t-static.ru/integration.js')
  document.head.appendChild(tinkoffScript)
  tinkoffScript.addEventListener('load', async () => {
    if (!('PaymentIntegration' in window)) {
      window.parent.postMessage('close')
      return
    }

    const key = useRuntimeConfig().public.tinkoffTerminalkey
    const initConfig = {
      terminalKey: typeof key === 'number' ? (key as number).toString() : key,
      product: 'eacq',
      features: {
        iframe: {},
      },
    }

    window.PaymentIntegration.init(initConfig)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then(async (integration: any) => {
        const MAIN_INTEGRATION_NAME = 'main-integration-name'
        const iframeConfig = {
          router: {
            redirectCallback: async (url: string) => {
              // Our SuccessURL/FailURL stays inside the iframe: ConfirmPayment reports the result via postMessage.
              // External pages (T-ID for T-Pay) forbid embedding via frame-ancestors, so leave the page
              // like integration.js does by default; ConfirmPayment handles the return outside the iframe.
              if (new URL(url, window.location.href).origin === window.location.origin) {
                container?.querySelector('iframe')?.setAttribute('src', url)
              } else {
                sessionStorage.setItem(PAYMENT_EXTERNAL_REDIRECT_KEY, 'true')
                window.location.href = url
              }
            },
          },
        }
        const mainPaymentIntegration = await integration.iframe.create(MAIN_INTEGRATION_NAME, iframeConfig)
        const container = document.getElementById('paymentContainer')
        await mainPaymentIntegration.mount(container, props.url)
        formLoaded.value = true
      })
      .catch((error: unknown) => {
        console.error('Payment integration error', error)
      })
  })
  changeWindowWidth()
  window.addEventListener('resize', changeWindowWidth)
  window.addEventListener('message', handleIframeMessage)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', changeWindowWidth)
  window.removeEventListener('message', handleIframeMessage)
})

const handleIframeMessage = (event: MessageEvent) => {
  console.log(event)
  if (event.data === 'success') {
    emit('success')
  // } else if (event.data === 'fail') {
  //   emit('fail', PaymentFailCode.TryAgain)
  } else if (event.data === 'close') {
    closeModal()
  } else if (typeof event.data === 'string' && event.data.includes('fail')) {
    const tinkoffErrorCode = parseInt(event.data.replace('fail-', ''))
    if (isNaN(tinkoffErrorCode)) {
      return
    }
    emit('fail', getPaymentFailCode(tinkoffErrorCode))
  }
}

const iframeWidth = computed(() => {
  if (windowWidth.value >= DESKTOP_WIDTH) {
    return 466
  }
  return windowWidth.value - 24
})

</script>

<template>
  <RModal name-modal="payment">
    <template #header>
      <h5 class="modal-title fs-5">
        {{ title }}
      </h5>
    </template>
    <template #body>
      <div
        v-if="!formLoaded"
        class="loading-area"
      >
        <div
          class="spinner-border"
          role="status"
        />
      </div>
      <div class="iframe-wrap">
        <div
          id="paymentContainer"
          :style="formLoaded ? {
            width: iframeWidth + 'px',
          } : {}"
        />
      </div>
    </template>
  </RModal>
</template>

<style lang="scss" scoped>
.spinner-border {
  color: $indigo-500;
}

.iframe-wrap {
   @include media-breakpoint-down(lg) {
    display: flex;
    justify-content: center;
    max-height: calc(100vh - 108px);
    overflow-y: auto;
  }

  @include media-breakpoint-down(md) {
    max-height: calc(100vh - 92px);
  }
}

.loading-area {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 490px;
  background: $indigo-100-light;
  margin-bottom: rem(16px);
}

:deep(.modal-body) {
  background: #f6f7f8;
  @include media-breakpoint-down(md) {
    padding-bottom: rem(24px);
  }
}

:deep(.modal-content) {
  @include media-breakpoint-down(md) {
    padding-bottom: 0;
  }  
}
</style>
